import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text, Tooltip } from '@radix-ui/themes';
import {
  DOT_COLORS,
  BROADCAST_DOT_COLORS,
  STATUS_CONFIG,
  BROADCAST_STATUS_CONFIG,
  getEffectiveDisplayStatus,
} from './helpers.js';
import styles from './CalendarView.module.css';

const EMPTY_LIST = [];
const noop = () => {};

export default function DayCell({
  dayNumber,
  campaigns = EMPTY_LIST,
  broadcasts = EMPTY_LIST,
  isToday = false,
  isPastDay = false,
  onCampaignClick = noop,
  onBroadcastClick = noop,
}) {
  const listRef = useRef(null);
  const [hasMoreContent, setHasMoreContent] = useState(false);

  const allItems = [
    ...campaigns.map(c => ({ ...c, itemType: 'campaign' })),
    ...broadcasts.map(b => ({ ...b, itemType: 'broadcast' })),
  ];

  useEffect(() => {
    const checkScrollable = () => {
      if (listRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = listRef.current;
        const isScrollable = scrollHeight > clientHeight;
        const isNotAtBottom = scrollTop + clientHeight < scrollHeight - 1;
        setHasMoreContent(isScrollable && isNotAtBottom);
      }
    };

    checkScrollable();

    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', checkScrollable);
      return () => listElement.removeEventListener('scroll', checkScrollable);
    }
  }, [campaigns, broadcasts]);

  const getBroadcastIcon = () => 'fa-regular fa-paper-plane';

  return (
    <Box
      className={`${styles.dayCell} ${isToday ? styles.dayCellToday : ''} ${
        isPastDay ? styles.dayCellPast : ''
      }`}
    >
      <Text className={styles.dayCellNumber} size="1" weight="medium">
        {dayNumber}
      </Text>
      <Box
        className={`${styles.dayCellItemsWrapper} ${
          hasMoreContent ? styles.dayCellItemsHasMore : ''
        }`}
      >
        <Flex
          ref={listRef}
          direction="column"
          gap="1"
          className={styles.dayCellItems}
        >
          {allItems.map((item, index) => {
            if (item.itemType === 'campaign') {
              const effectiveStatus = getEffectiveDisplayStatus(item);
              const statusLabel =
                STATUS_CONFIG[effectiveStatus]?.label || effectiveStatus;
              const label = `${statusLabel}: ${item.name}`;

              return (
                <Tooltip key={`campaign-${item.uuid}-${index}`} content={label}>
                  <Flex
                    className={styles.dayCellItem}
                    align="center"
                    gap="1"
                    onClick={e => {
                      e.stopPropagation();
                      onCampaignClick(item);
                    }}
                    style={{
                      borderLeft: `2px solid ${DOT_COLORS[effectiveStatus]}`,
                    }}
                  >
                    <i
                      className={`fa-regular fa-star ${styles.dayCellItemIcon}`}
                    />
                    <Text size="1" className={styles.dayCellItemText}>
                      {item.name.length > 12
                        ? `${item.name.slice(0, 10)}…`
                        : item.name}
                    </Text>
                  </Flex>
                </Tooltip>
              );
            } else {
              return (
                <Tooltip
                  key={`broadcast-${item.uuid}-${index}`}
                  content={`${BROADCAST_STATUS_CONFIG[item.status]?.label ||
                    item.status} ${
                    item.broadcastTypeLabel
                  }: ${item.contentPreview || ''}`}
                >
                  <Flex
                    className={styles.dayCellItem}
                    align="center"
                    gap="1"
                    onClick={e => {
                      e.stopPropagation();
                      onBroadcastClick(item);
                    }}
                    style={{
                      borderLeft: `2px solid ${
                        BROADCAST_DOT_COLORS[item.status]
                      }`,
                    }}
                  >
                    <i
                      className={`${getBroadcastIcon(item.broadcastType)} ${
                        styles.dayCellItemIcon
                      }`}
                    />
                    <Text size="1" className={styles.dayCellItemText}>
                      {(item.contentPreview || '').length > 12
                        ? `${(item.contentPreview || '').slice(0, 10)}…`
                        : item.contentPreview || item.broadcastTypeLabel}
                    </Text>
                  </Flex>
                </Tooltip>
              );
            }
          })}
        </Flex>
      </Box>
    </Box>
  );
}

DayCell.propTypes = {
  dayNumber: PropTypes.number.isRequired,
  campaigns: PropTypes.array,
  broadcasts: PropTypes.array,
  isToday: PropTypes.bool,
  isPastDay: PropTypes.bool,
  onCampaignClick: PropTypes.func,
  onBroadcastClick: PropTypes.func,
};
